import { Grid, IconButton, Typography } from "@material-ui/core";
import { useMemo, useState } from "react";
import { BsArrowLeft } from "react-icons/bs";
import CustomModal from "shared/components/modal/CustomModal";
import { Condominio } from "./ModalReplicarAcesso";
import modalAcessoVinculadoStyles from "./styles/ModalAcessoVinculadoStyles";
import ModalConfiguracaoCertificado from "./ModalConfiguracaoCertificado";
import TabelaConfigurarCondominios from "./TabelaConfigurarCondominios";
import { limparCpfCnpj } from "shared/util/functions/formatacoesDeValores/FormatarValores";

type ModalAcessoVinculadoProps = {
  condominiosVinculados: Condominio[];
  setCondominiosSelecionados: React.Dispatch<React.SetStateAction<Condominio[]>>;
  setIdsCondominiosSelecionados: React.Dispatch<React.SetStateAction<number[]>>;
  openModal: boolean;
  loginPrefeitura: string;
  senhaPrefeitura: string;
  setShowModalAcessoVinculado: (value: boolean) => void;
  setShowModalConfiguracaoBuscaExterna: (value: boolean) => void;
  setShowModalReplicar: (value: boolean) => void;
};

const prepararParaBuscar = (texto: string) => {
  return texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
};

const ModalAcessoVinculado = ({
  openModal,
  condominiosVinculados,
  setCondominiosSelecionados,
  setIdsCondominiosSelecionados,
  setShowModalAcessoVinculado,
  setShowModalConfiguracaoBuscaExterna,
  setShowModalReplicar,
  loginPrefeitura,
  senhaPrefeitura,
}: ModalAcessoVinculadoProps) => {
  const [searchCondominio, setSearchCondominio] = useState<string>("");
  const [showModalConfiguracaoCertificado, setShowModalConfiguracaoCertificado] = useState(false);
  const [condominioSelecionado, setCondominioSelecionado] = useState<Condominio | null>(null);
  const [sortString, setSortString] = useState<string>("");

  const styles = modalAcessoVinculadoStyles();

  const handleClose = () => {
    setShowModalAcessoVinculado(false);
    setSearchCondominio("");
  };

  const openModalConfiguracaoCertificado = (condominio: Condominio) => { 
    setShowModalConfiguracaoCertificado(true);
    setCondominioSelecionado(condominio);
  };

  const busca = prepararParaBuscar(searchCondominio);
  const buscaPorDocumento = limparCpfCnpj(searchCondominio);

  const condominiosFiltrados = condominiosVinculados.filter((condominio: Condominio) => {
    const nomeNormalizado = prepararParaBuscar(condominio.nomeFantasia);
    const cnpjNormalizado = limparCpfCnpj(condominio.cnpj ?? "") || "";

    const matchDocumento =
      buscaPorDocumento.length > 0 && cnpjNormalizado.includes(buscaPorDocumento);

    return nomeNormalizado.includes(busca) || matchDocumento;
  });

  const condominiosOrdenados = useMemo(() => {
    const listaParaOrdenar = [...condominiosFiltrados];

    switch (sortString) {
      case 'nomeFantasia,ASC':
        return listaParaOrdenar.sort((a, b) => a.nomeFantasia.localeCompare(b.nomeFantasia));
      case 'nomeFantasia,DESC':
        return listaParaOrdenar.sort((a, b) => b.nomeFantasia.localeCompare(a.nomeFantasia));
      case 'cnpj,ASC':
        return listaParaOrdenar.sort((a, b) =>
          limparCpfCnpj(a.cnpj ?? "").localeCompare(limparCpfCnpj(b.cnpj ?? ""))
        );
      case 'cnpj,DESC':
        return listaParaOrdenar.sort((a, b) =>
          limparCpfCnpj(b.cnpj ?? "").localeCompare(limparCpfCnpj(a.cnpj ?? ""))
        );
      default:
        return condominiosFiltrados;
    }
  }, [condominiosFiltrados, sortString]);

  const Content = () => {
    return (
      <Grid container spacing={1}>
        <div style={{ marginBottom: "0.5rem" }}>
          <IconButton onClick={handleClose} size="small">
            <BsArrowLeft size={25} />
          </IconButton>
        </div>
        <Grid item xs={12}>
          <Typography variant="h6">Configuração do certificado</Typography>
          <Typography variant="body2">
            O acesso ao sistema da prefeitura informado será replicado a outros condomínios.
          </Typography>
        </Grid>
        <Grid item xs={12} className={styles.containerEspacamento}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="body1" className={styles.textoBody1}>
              Selecione os condomínios
            </Typography>
            <Typography
              variant="body1"
              className={styles.textoBody1Color}
              onClick={() => {
                handleClose();
                setCondominiosSelecionados([]);
                setIdsCondominiosSelecionados([]);
              }}
            >
              + Alterar condomínios selecionados
            </Typography>
          </div>
          <TabelaConfigurarCondominios
            condominios={condominiosOrdenados}
            searchCondominio={searchCondominio}
            setSearchCondominio={setSearchCondominio}
            openModal={openModalConfiguracaoCertificado}
            sizeInputSearch={8}
            setSortString={setSortString}
          />
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <CustomModal
        showModal={openModal}
        titulo="Acesso vinculado para os condomínios"
        size="lg"
        onCloseModal={handleClose}
        tipoModal="MODAL_LATERAL"
        scrollable
        hideCloseButton
        customFooter={null}
      >
        <Grid container spacing={2}>
          <Grid item style={{ marginBottom: "1rem", padding: "0.8rem" }}>
            <Content />
          </Grid>
        </Grid>
      </CustomModal>
      {showModalConfiguracaoCertificado && (
        <ModalConfiguracaoCertificado
          openModal={showModalConfiguracaoCertificado}
          setShowModalConfiguracaoCertificado={setShowModalConfiguracaoCertificado}
          condominioSelecionado={condominioSelecionado}
          setCondominioSelecionado={setCondominioSelecionado}
          condominiosParaConfigurar={condominiosOrdenados}
          setShowModalConfiguracaoBuscaExterna={setShowModalConfiguracaoBuscaExterna}
          setShowModalReplicar={setShowModalReplicar}
          setShowModalAcessoVinculado={setShowModalAcessoVinculado}
          loginPrefeitura={loginPrefeitura}
          senhaPrefeitura={senhaPrefeitura}
        />
      )}
    </>
  );
};

export default ModalAcessoVinculado;
